import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Siren } from "lucide-react";

export default function EmergencyControl() {
  const [state, setState] = useState(null);
  const [zone, setZone] = useState("");
  const [reason, setReason] = useState("");
  const [holding, setHolding] = useState(0);

  useEffect(() => {
    api.get("/emergency").then(({ data }) => setState(data));
  }, []);

  // Press-and-hold (1.2s) to engage
  const startHold = () => {
    const start = Date.now();
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      setHolding(Math.min(100, (elapsed / 1200) * 100));
      if (elapsed >= 1200) {
        clearInterval(iv);
        engage(true);
      }
    }, 40);
    window._binHoldIv = iv;
  };
  const cancelHold = () => {
    clearInterval(window._binHoldIv);
    setHolding(0);
  };

  const engage = async (active) => {
    try {
      const { data } = await api.post("/emergency", {
        active, zone: zone || null, reason: reason || null,
      });
      setState(data);
      toast.success(active ? "EMERGENCY MODE ENGAGED" : "Emergency mode released");
      setHolding(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setHolding(0);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl">
      <div className="mb-6">
        <div className="bin-label">PRIORITY RECONFIG</div>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Emergency Mode Control</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Engaging deprioritizes non-critical sessions network-wide. Hold the action button to confirm.
        </p>
      </div>

      <div className="bin-card p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="bin-label">CURRENT STATE</div>
            <div className={`text-3xl font-black mt-1 tracking-tighter ${state?.active ? "text-red-400" : "text-emerald-400"}`}>
              {state?.active ? "ENGAGED" : "NORMAL"}
            </div>
          </div>
          <Siren className={`w-12 h-12 ${state?.active ? "text-red-400" : "text-zinc-700"}`} />
        </div>

        {state?.active ? (
          <div>
            <div className="text-sm text-zinc-400 mb-4">
              <div>Zone: <span className="font-mono">{state.zone || "network-wide"}</span></div>
              <div>Reason: <span className="font-mono">{state.reason || "—"}</span></div>
              <div>By: <span className="font-mono">{state.activated_by}</span></div>
            </div>
            <button data-testid="emergency-release" onClick={() => engage(false)} className="bin-btn bin-btn-primary">
              Release Emergency Mode
            </button>
          </div>
        ) : (
          <div>
            <label className="bin-label">Zone (optional)</label>
            <input
              data-testid="emergency-zone"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              placeholder="e.g. Brooklyn-East"
              className="w-full mt-2 mb-4 bg-[#18181b] border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
            />
            <label className="bin-label">Reason</label>
            <input
              data-testid="emergency-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grid outage, heat dome, etc."
              className="w-full mt-2 mb-6 bg-[#18181b] border border-zinc-800 rounded px-3 py-2 text-sm font-mono"
            />

            <button
              data-testid="emergency-engage-hold"
              onMouseDown={startHold} onTouchStart={startHold}
              onMouseUp={cancelHold} onMouseLeave={cancelHold} onTouchEnd={cancelHold}
              className="w-full relative overflow-hidden bin-btn bin-btn-danger text-base py-4"
            >
              <div className="absolute inset-0 bg-red-500" style={{ width: `${holding}%`, opacity: 0.4, transition: "width 0.05s linear" }} />
              <span className="relative">HOLD TO ENGAGE EMERGENCY MODE</span>
            </button>
            <p className="mt-3 text-xs text-zinc-500 font-mono">Hold for 1.2s — this is a destructive operation.</p>
          </div>
        )}
      </div>
    </div>
  );
}
